import { findStateIcon } from "@/assets/states/StateIcon";
import { commonComponents as cc } from "@/components/common";
import { ui } from "@/components/ui";
import { findLatestEntry } from "@/features/diaries/services/flattenEntriesView";
import { cn } from "@/lib/utils";
import type { StatefulDiaryT } from "@/types/database/diaries";
import dayjs from "dayjs";
import { User } from "lucide-react";
import { useState } from "react";
import IconDetail from "../../../../../components/common/IconDetail";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import DiaryNameForm from "../../../../diaries/features/edit/components/DiaryNameForm";
import DiaryOrganisationForm from "../../../../diaries/features/edit/components/DiaryOrganisationForm";
import SharedAttributeContactPerson from "../../../../shared_attributes/components/SharedAttributeContactPerson";
import ContactPerson from "./ContactPerson";

interface Props {
	diary: StatefulDiaryT;
}

const StateCard = (props: Props) => {
	const diary = props.diary;
	if (!diary) return null;
	const state = props.diary.state;
	const [editing, setEditing] = useState(false);

	// Determine card styling based on diary status
	const getCardStyling = () => {
		const status = diary.diary_status || "UNKNOWN";

		if (["NEVER_WORKED", "HIGH", "LOW"].includes(status)) {
			return "border-red-200 bg-red-50";
		}

		if (["PAIN_SPIKE"].includes(status)) {
			return "border-amber-200 bg-amber-50";
		}

		if (["PAIN_REDUCED", "READY_TO_REVIEW"].includes(status)) {
			return "border-green-200 bg-green-50";
		}

		if (["ACTIVE"].includes(status)) {
			return "border-slate-200 bg-white";
		}

		// Default styling for unknown status
		return "border-gray-200 bg-gray-50";
	};

	// Handle case where state is undefined
	if (!state) {
		return (
			<ui.Card className={cn("w-full", "border-gray-200 bg-gray-50")}>
				<ui.CardHeader className="flex flex-row justify-between items-start w-full">
					<div className="flex flex-row gap-x-2 space-y-0">
						<h2
							data-testid="state-name"
							className="mt-0 w-fit font-medium text-slate-700 dark:text-slate-200 text-xl"
						>
							Unknown State
						</h2>
					</div>
					<Button
						className="mt-0 p-0 h-fit"
						variant="link"
						size="md"
						onClick={() => setEditing(!editing)}
					>
						{editing ? "Done" : "Edit"}
					</Button>
				</ui.CardHeader>
				{editing ? (
					<ui.CardContent className="flex flex-col gap-2">
						<cc.EditDialog label="Diary Name" info={diary.diary_name}>
							<DiaryNameForm diary={diary} diaryId={diary.diary_id} />
						</cc.EditDialog>

						<cc.EditDialog
							label="Diary Organisation"
							info={diary.organisation_name}
						>
							<DiaryOrganisationForm diary={diary} />
						</cc.EditDialog>
					</ui.CardContent>
				) : (
					<>
						<ui.CardContent className="flex flex-col gap-2">
							<div className="flex flex-row justify-between items-center mt-0 w-full">
								<h3 className="text-md text-slate-900 dark:text-slate-100">
									{props.diary.diary_name}
								</h3>
								<p className="font-medium text-slate-600 dark:text-slate-300 text-sm">
									{props.diary.start_date
										? dayjs(props.diary.start_date).format("DD MMM YYYY")
										: "No Start Date"}{" "}
									-{" "}
									{props.diary.end_date
										? dayjs(props.diary.end_date).format("DD MMM YYYY")
										: "Now"}
								</p>
							</div>

							{findLatestEntry(props.diary) && (
								<p>
									Last entry{" "}
									{dayjs(
										findLatestEntry(props.diary)?.calculated_entry_time,
									).fromNow()}
								</p>
							)}
						</ui.CardContent>
						<ui.CardFooter>
							<Badge variant="secondary">{props.diary.organisation_name}</Badge>
							<Badge variant="outline" className="ml-2">
								Unknown State
							</Badge>
							<Badge variant="outline" className="ml-2">
								{props.diary.contact_name}
							</Badge>
						</ui.CardFooter>
					</>
				)}
			</ui.Card>
		);
	}

	return (
		<ui.Card className={cn("w-full", getCardStyling())}>
			<ui.CardHeader className="flex flex-row justify-between items-start w-full">
				<div className="flex flex-row gap-x-2 space-y-0 [&_svg]:w-[40px] [&_svg]:h-[40px]">
					{findStateIcon(state.state_key)}
					<h2
						data-testid="state-name"
						className="mt-0 w-fit font-medium text-slate-700 dark:text-slate-200 text-xl"
					>
						{state.name}
					</h2>
				</div>
				<Button
					className="mt-0 p-0 h-fit"
					variant="link"
					size="md"
					onClick={() => setEditing(!editing)}
				>
					{editing ? "Done" : "Edit"}
				</Button>
			</ui.CardHeader>
			{editing ? (
				<ui.CardContent className="flex flex-col gap-2">
					<cc.EditDialog label="Diary Name" info={diary.diary_name}>
						<DiaryNameForm diary={diary} diaryId={diary.diary_id} />
					</cc.EditDialog>

					<cc.EditDialog
						label="Diary Organisation"
						info={diary.organisation_name}
					>
						<DiaryOrganisationForm diary={diary} />
					</cc.EditDialog>
					<cc.EditDialog label="Contact Name" info={diary.contact_name}>
						<SharedAttributeContactPerson
							sharedAttributes={diary.shared_attributes}
							organisationId={diary.organisation_id}
						/>
					</cc.EditDialog>
				</ui.CardContent>
			) : (
				<>
					<ui.CardContent className="flex flex-col gap-2">
						<div className="flex flex-row justify-between items-center mt-0 w-full">
							<h3 className="text-md text-slate-900 dark:text-slate-100">
								{props.diary.diary_name}
							</h3>
							<p className="font-medium text-slate-600 dark:text-slate-300 text-sm">
								{props.diary.start_date
									? dayjs(props.diary.start_date).format("DD MMM YYYY")
									: "No Start Date"}{" "}
								-{" "}
								{props.diary.end_date
									? dayjs(props.diary.end_date).format("DD MMM YYYY")
									: "Now"}
							</p>
						</div>

						{findLatestEntry(props.diary) && (
							<p>
								Last entry{" "}
								{dayjs(
									findLatestEntry(props.diary)?.calculated_entry_time,
								).fromNow()}
							</p>
						)}
					</ui.CardContent>
					<ui.CardFooter>
						<Badge variant="secondary">{props.diary.organisation_name}</Badge>
						{!props.diary.diary_status && (
							<Badge variant="outline" className="ml-2">
								Unknown Status
							</Badge>
						)}
						<Badge variant="outline" className="ml-2">
							{props.diary.contact_name}
						</Badge>
					</ui.CardFooter>
				</>
			)}
		</ui.Card>
	);
};

export default StateCard;
